import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSessionStore } from '@stores/sessionStore';
import { useDomainStore } from '@stores/domainStore';
import { useVerificationStore } from '@stores/verificationStore';
import { useEffect, useRef } from 'react';
import { useSignalR } from '@hooks/useSignalR';
import Layout from '@components/Layout';
import NamePromptModal from '@components/NamePromptModal';
import PhoneVerificationModal from '@components/PhoneVerificationModal';
import SessionSignIn from '@components/SessionSignIn';
import { ModuleGuard, Modules } from '@components/ModuleGuard';
import OrderStatusPage from './pages/OrderStatusPage';
import ActivatePage from './pages/ActivatePage';
import GuestLinkPage from './pages/GuestLinkPage';
import RegistrationPage from './pages/RegistrationPage';
import DocumentsPage from './pages/DocumentsPage';
import ReviewPage from './pages/ReviewPage';
import LostPropertyPage from './pages/LostPropertyPage';
import WelcomePage from './pages/WelcomePage';
import StationOrderPage from './pages/StationOrderPage';
import HousekeepingPage from './pages/HousekeepingPage';
import MaintenancePage from './pages/MaintenancePage';
import LaundryPage from './pages/LaundryPage';
import GuidesPage from './pages/GuidesPage';
import MessagesPage from './pages/MessagesPage';
import NotificationsPage from './pages/NotificationsPage';
import VouchersPage from './pages/VouchersPage';
import ProfilePage from './pages/ProfilePage';
import MyRoomsPage from './pages/MyRoomsPage';
import FolioPage from './pages/FolioPage';
import PaymentCallbackPage from './pages/PaymentCallbackPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import DataDeletionPage from './pages/DataDeletionPage';
import VoiceCallPage from './pages/VoiceCallPage';
import KioskPage from './pages/KioskPage';
import KioskPayPage from './pages/KioskPayPage';
import DeviceClaimPage from './pages/DeviceClaimPage';
import LoadingSpinner from '@components/LoadingSpinner';
import { getAuthStatus } from '@services/authService';

function App() {
  const location = useLocation();
  const { isLoading: sessionLoading, isValid, validateSession, refreshSession, clearSession, needsNamePrompt } = useSessionStore();
  const { 
    isLoading: domainLoading, 
    isResolved, 
    error: domainError, 
    resolveDomain,
    getBasePath 
  } = useDomainStore();
  const { openModal: openVerification } = useVerificationStore();
  const { on } = useSignalR();

  // Track if we're on the activate page
  const isActivatePage = location.pathname.includes('/activate');

  // Pre-check-in flow is anonymous — bootstraps its own JWT from the email-link token.
  // Don't gate it on the in-stay session/domain resolution.
  // The pre-check-in link and the stay link (sent by automated messages before, during or after the
  // stay) both enter the portal through the reservation.
  const isPreCheckInPage = location.pathname.startsWith('/precheckin') || location.pathname === '/stay';

  // Both the /activate and /precheckin surfaces are standalone: they manage their own token
  // and must be kept clear of the in-stay session machinery below. Critically, the session
  // effect grabs ?token= and strips the query string — on /precheckin that would steal the
  // pre-check-in token before the activate page can read it, so it must bail out here too.
  // The kiosk tablet's screen: no guest is signed in — it authenticates as the kiosk device.
  const isKioskPage = location.pathname === '/kiosk' || location.pathname.startsWith('/kiosk/');
  // The short guest link every message carries (/s/<handle>) — it signs the browser in by code itself.
  const isGuestLinkPage = location.pathname.startsWith('/s/');

  const isStandalonePage = isActivatePage || isPreCheckInPage || isKioskPage || isGuestLinkPage;

  // Track if we're on legal pages (no auth required)
  const isLegalPage = location.pathname.startsWith('/legal/');

  if (isKioskPage) {
    return <KioskPage />;
  }

  // Handle legal pages (no auth required) - render immediately
  if (isLegalPage) {
    return (
      <div className="legal-page-wrapper">
        <Routes>
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          <Route path="/legal/data-deletion" element={<DataDeletionPage />} />
        </Routes>
      </div>
    );
  }

  // Resolve domain/slug when not on a standalone (activate / pre-check-in) page
  useEffect(() => {
    if (isStandalonePage) {
      return;
    }
    // Trigger domain resolution when navigating away from activate or on initial load
    if (!isResolved) {
      resolveDomain();
    }
  }, [resolveDomain, isStandalonePage, isResolved]);

  // Track if we've already validated THIS page load (to prevent infinite re-validation)
  const hasValidatedOnMount = useRef(false);
  
  useEffect(() => {
    // Don't auto-validate on standalone pages (activate / pre-check-in) — they own their token.
    // This guard is critical for /precheckin: the ?token= grab + replaceState below would
    // otherwise strip the pre-check-in token out of the URL before its page can read it.
    if (isStandalonePage) {
      return;
    }

    // Wait for domain resolution before validating session
    if (!isResolved) return;

    // Extract token from URL on initial load
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      // Store token and remove from URL
      localStorage.setItem('gcxp_token', token);
      window.history.replaceState({}, '', window.location.pathname);
    }

    // ALWAYS validate on mount to get fresh permission data from server
    // This ensures admin permission changes take effect immediately
    // Only skip if we've ALREADY validated during THIS page load
    if (hasValidatedOnMount.current) return;
    hasValidatedOnMount.current = true;

    validateSession();
  }, [validateSession, isResolved, isStandalonePage]);

  // Real-time session updates via SignalR — replaces 2-minute polling
  useEffect(() => {
    if (!isValid || isStandalonePage) return;

    const unsubUpdated = on('SessionUpdated', () => {
      refreshSession(); // silently sync new expiresAt / checkOutAt
    });

    const unsubRevoked = on('SessionRevoked', () => {
      clearSession(); // immediately log out the guest
    });

    return () => {
      unsubUpdated();
      unsubRevoked();
    };
  }, [isValid, isStandalonePage, on, refreshSession, clearSession]);

  // Listen for 403 INSUFFICIENT_TRUST_LEVEL from API interceptor
  useEffect(() => {
    const handler = () => openVerification();
    window.addEventListener('gcxp:verification-required', handler);
    return () => window.removeEventListener('gcxp:verification-required', handler);
  }, [openVerification]);

  // Listen for 401 from any API call (token rejected by the guest auth handler). Re-validate
  // against the server: a genuinely expired/checked-out/revoked session flips isValid=false and
  // renders <SessionSignIn /> so the guest can re-authenticate, instead of being stuck on a
  // generic "failed, try again" toast. A transient/false 401 self-heals (validate confirms the
  // session is still good) so we don't log out a still-valid guest.
  useEffect(() => {
    const handler = () => { validateSession(); };
    window.addEventListener('gcxp:session-expired', handler);
    return () => window.removeEventListener('gcxp:session-expired', handler);
  }, [validateSession]);

  // When the guest lands unverified but a code was already pushed (e.g. by the front desk
  // after check-in), prompt them to enter it — opens the modal straight at code entry.
  const hasCheckedPending = useRef(false);
  useEffect(() => {
    if (!isValid || isStandalonePage || hasCheckedPending.current) return;
    hasCheckedPending.current = true;
    getAuthStatus().then((status) => {
      if (status.success && !status.isVerified && status.pendingChallenge) {
        openVerification(undefined, {
          channel: status.pendingChallenge.channel,
          maskedPhone: status.pendingChallenge.maskedPhone,
        });
      }
    });
  }, [isValid, isStandalonePage, openVerification]);

  // Handle activate route separately (no auth required)
  if (isActivatePage) {
    const basePath = getBasePath();
    return (
      <Routes>
        <Route path={`${basePath}/activate`} element={<ActivatePage />} />
        <Route path="/activate" element={<ActivatePage />} />
      </Routes>
    );
  }

  // The pre-check-in link (email / WhatsApp / SMS). It is a way INTO the portal, not a separate form:
  // the signed reservation token leads to the stay's session, this browser is registered on it like a
  // room QR would, and the guest lands on the normal portal with Registration and Documents as modules.
  // Any old /precheckin/<step> URL goes to the same place.
  if (isPreCheckInPage) {
    return (
      <Routes>
        <Route path="/precheckin" element={<ActivatePage mode="reservation" />} />
        <Route path="/stay" element={<ActivatePage mode="reservation" />} />
        <Route path="/precheckin/*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  if (isGuestLinkPage) {
    return (
      <Routes>
        <Route path="/s/:handle" element={<GuestLinkPage />} />
      </Routes>
    );
  }

  // If session is already valid (e.g., just activated), skip domain loading and render routes
  if (isValid && !sessionLoading) {
    const basePath = getBasePath();
    // Get the guest name from router state (passed during activation) for the name prompt
    const locationState = location.state as { guestName?: string } | null;
    return (
      <>
        {needsNamePrompt && <NamePromptModal guestName={locationState?.guestName} />}
        <PhoneVerificationModal />
        <Layout>
        <Routes>
          {/* Routes with slug prefix (platform domain) */}
          <Route path={`${basePath}`} element={<Navigate to={`${basePath}/welcome`} replace />} />
          <Route path={`${basePath}/`} element={<Navigate to={`${basePath}/welcome`} replace />} />
          <Route path={`${basePath}/welcome`} element={<WelcomePage />} />
          <Route path={`${basePath}/station/:stationId`} element={
            <ModuleGuard requiredModule={Modules.FnB}><StationOrderPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/kiosk-pay/:token`} element={<KioskPayPage />} />
          <Route path={`${basePath}/device/:token`} element={<DeviceClaimPage />} />
          <Route path={`${basePath}/housekeeping`} element={
            <ModuleGuard requiredModule={Modules.Housekeeping}><HousekeepingPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/maintenance`} element={
            <ModuleGuard requiredModule={Modules.Maintenance}><MaintenancePage /></ModuleGuard>
          } />
          <Route path={`${basePath}/laundry`} element={
            <ModuleGuard requiredModule={Modules.Laundry}><LaundryPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/guides`} element={
            <ModuleGuard requiredModule={Modules.Guides}><GuidesPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/messages`} element={
            <ModuleGuard requiredModule={Modules.Messages}><MessagesPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/registration`} element={
            <ModuleGuard requiredModule={Modules.Registration}><RegistrationPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/documents`} element={
            <ModuleGuard requiredModule={Modules.Documents}><DocumentsPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/review`} element={
            <ModuleGuard requiredModule={Modules.Review}><ReviewPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/lost_property`} element={
            <ModuleGuard requiredModule={Modules.LostProperty}><LostPropertyPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/voice-call`} element={
            <ModuleGuard requiredModule={Modules.VoiceCall}><VoiceCallPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/notifications`} element={<NotificationsPage />} />
          <Route path={`${basePath}/profile`} element={<ProfilePage />} />
          <Route path={`${basePath}/register-rooms`} element={<MyRoomsPage />} />
          <Route path={`${basePath}/my-rooms`} element={<MyRoomsPage />} />
          <Route path={`${basePath}/vouchers`} element={
            <ModuleGuard requiredModule={Modules.Vouchers}><VouchersPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/folio`} element={
            <ModuleGuard requiredModule={Modules.Folio}><FolioPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/payment/success`} element={
            <ModuleGuard requiredModule={Modules.Folio}><PaymentCallbackPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/payment/error`} element={
            <ModuleGuard requiredModule={Modules.Folio}><PaymentCallbackPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/payment/cancel`} element={
            <ModuleGuard requiredModule={Modules.Folio}><PaymentCallbackPage /></ModuleGuard>
          } />
          <Route path={`${basePath}/order/:orderId`} element={
            <ModuleGuard requiredModule={Modules.FnB}><OrderStatusPage /></ModuleGuard>
          } />
          
          {/* Fallback routes without prefix (custom domain) */}
          <Route path="/" element={<Navigate to="/welcome" replace />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/station/:stationId" element={
            <ModuleGuard requiredModule={Modules.FnB}><StationOrderPage /></ModuleGuard>
          } />
          <Route path="/kiosk-pay/:token" element={<KioskPayPage />} />
          <Route path="/device/:token" element={<DeviceClaimPage />} />
          <Route path="/housekeeping" element={
            <ModuleGuard requiredModule={Modules.Housekeeping}><HousekeepingPage /></ModuleGuard>
          } />
          <Route path="/maintenance" element={
            <ModuleGuard requiredModule={Modules.Maintenance}><MaintenancePage /></ModuleGuard>
          } />
          <Route path="/laundry" element={
            <ModuleGuard requiredModule={Modules.Laundry}><LaundryPage /></ModuleGuard>
          } />
          <Route path="/guides" element={
            <ModuleGuard requiredModule={Modules.Guides}><GuidesPage /></ModuleGuard>
          } />
          <Route path="/messages" element={
            <ModuleGuard requiredModule={Modules.Messages}><MessagesPage /></ModuleGuard>
          } />
          <Route path="/registration" element={
            <ModuleGuard requiredModule={Modules.Registration}><RegistrationPage /></ModuleGuard>
          } />
          <Route path="/documents" element={
            <ModuleGuard requiredModule={Modules.Documents}><DocumentsPage /></ModuleGuard>
          } />
          <Route path="/review" element={
            <ModuleGuard requiredModule={Modules.Review}><ReviewPage /></ModuleGuard>
          } />
          <Route path="/lost_property" element={
            <ModuleGuard requiredModule={Modules.LostProperty}><LostPropertyPage /></ModuleGuard>
          } />
          <Route path="/voice-call" element={
            <ModuleGuard requiredModule={Modules.VoiceCall}><VoiceCallPage /></ModuleGuard>
          } />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/register-rooms" element={<MyRoomsPage />} />
          <Route path="/my-rooms" element={<MyRoomsPage />} />
          <Route path="/vouchers" element={
            <ModuleGuard requiredModule={Modules.Vouchers}><VouchersPage /></ModuleGuard>
          } />
          <Route path="/folio" element={
            <ModuleGuard requiredModule={Modules.Folio}><FolioPage /></ModuleGuard>
          } />
          <Route path="/payment/success" element={
            <ModuleGuard requiredModule={Modules.Folio}><PaymentCallbackPage /></ModuleGuard>
          } />
          <Route path="/payment/error" element={
            <ModuleGuard requiredModule={Modules.Folio}><PaymentCallbackPage /></ModuleGuard>
          } />
          <Route path="/payment/cancel" element={
            <ModuleGuard requiredModule={Modules.Folio}><PaymentCallbackPage /></ModuleGuard>
          } />
          <Route path="/order/:orderId" element={
            <ModuleGuard requiredModule={Modules.FnB}><OrderStatusPage /></ModuleGuard>
          } />
          
          {/* Legacy routes with just hotel slug */}
          <Route path="/:hotelSlug/welcome" element={<WelcomePage />} />
          <Route path="/:hotelSlug/station/:stationId" element={
            <ModuleGuard requiredModule={Modules.FnB}><StationOrderPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/housekeeping" element={
            <ModuleGuard requiredModule={Modules.Housekeeping}><HousekeepingPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/maintenance" element={
            <ModuleGuard requiredModule={Modules.Maintenance}><MaintenancePage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/laundry" element={
            <ModuleGuard requiredModule={Modules.Laundry}><LaundryPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/guides" element={
            <ModuleGuard requiredModule={Modules.Guides}><GuidesPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/messages" element={
            <ModuleGuard requiredModule={Modules.Messages}><MessagesPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/registration" element={
            <ModuleGuard requiredModule={Modules.Registration}><RegistrationPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/documents" element={
            <ModuleGuard requiredModule={Modules.Documents}><DocumentsPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/review" element={
            <ModuleGuard requiredModule={Modules.Review}><ReviewPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/lost_property" element={
            <ModuleGuard requiredModule={Modules.LostProperty}><LostPropertyPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/voice-call" element={
            <ModuleGuard requiredModule={Modules.VoiceCall}><VoiceCallPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/notifications" element={<NotificationsPage />} />
          <Route path="/:hotelSlug/profile" element={<ProfilePage />} />
          <Route path="/:hotelSlug/register-rooms" element={<MyRoomsPage />} />
          <Route path="/:hotelSlug/my-rooms" element={<MyRoomsPage />} />
          <Route path="/:hotelSlug/vouchers" element={
            <ModuleGuard requiredModule={Modules.Vouchers}><VouchersPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/folio" element={
            <ModuleGuard requiredModule={Modules.Folio}><FolioPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/payment/success" element={
            <ModuleGuard requiredModule={Modules.Folio}><PaymentCallbackPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/payment/error" element={
            <ModuleGuard requiredModule={Modules.Folio}><PaymentCallbackPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/payment/cancel" element={
            <ModuleGuard requiredModule={Modules.Folio}><PaymentCallbackPage /></ModuleGuard>
          } />
          <Route path="/:hotelSlug/order/:orderId" element={
            <ModuleGuard requiredModule={Modules.FnB}><OrderStatusPage /></ModuleGuard>
          } />
          
          <Route path="*" element={<Navigate to={`${basePath}/welcome`} replace />} />
        </Routes>
      </Layout>
      </>
    );
  }

  // Session loading
  if (sessionLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <LoadingSpinner />
      </div>
    );
  }

  // Domain resolution loading (only check when session is not valid)
  if (domainLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <LoadingSpinner />
      </div>
    );
  }

  // Domain resolution error
  if (domainError) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center p-4">
          <h1 className="h4 mb-3">Configuration Error</h1>
          <p className="text-muted">{domainError}</p>
        </div>
      </div>
    );
  }

  if (!isValid) {
    // No valid session (expired, or the guest opened the app without scanning a QR / link).
    // Offer token-less sign-in: enter phone → receive a code → the code mints the session.
    return <SessionSignIn />;
  }

  // Build routes with optional chain/hotel slug prefix
  const basePath = getBasePath();

  return (
    <>
    {needsNamePrompt && <NamePromptModal />}
    <PhoneVerificationModal />
    <Layout>
      <Routes>
        {/* Routes with slug prefix (platform domain) */}
        <Route path={`${basePath}`} element={<Navigate to={`${basePath}/welcome`} replace />} />
        <Route path={`${basePath}/`} element={<Navigate to={`${basePath}/welcome`} replace />} />
        <Route path={`${basePath}/welcome`} element={<WelcomePage />} />
        <Route path={`${basePath}/housekeeping`} element={<HousekeepingPage />} />
        <Route path={`${basePath}/maintenance`} element={<MaintenancePage />} />
        <Route path={`${basePath}/laundry`} element={<LaundryPage />} />
        <Route path={`${basePath}/guides`} element={<GuidesPage />} />
        <Route path={`${basePath}/voice-call`} element={<VoiceCallPage />} />
        <Route path={`${basePath}/folio`} element={<FolioPage />} />
        <Route path={`${basePath}/payment/success`} element={<PaymentCallbackPage />} />
        <Route path={`${basePath}/payment/error`} element={<PaymentCallbackPage />} />
        <Route path={`${basePath}/payment/cancel`} element={<PaymentCallbackPage />} />
        <Route path={`${basePath}/order/:orderId`} element={<OrderStatusPage />} />
        
        {/* Fallback routes without prefix (custom domain) */}
        <Route path="/" element={<Navigate to="/welcome" replace />} />
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/housekeeping" element={<HousekeepingPage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
        <Route path="/laundry" element={<LaundryPage />} />
        <Route path="/guides" element={<GuidesPage />} />
        <Route path="/voice-call" element={<VoiceCallPage />} />
        <Route path="/folio" element={<FolioPage />} />
        <Route path="/payment/success" element={<PaymentCallbackPage />} />
        <Route path="/payment/error" element={<PaymentCallbackPage />} />
        <Route path="/payment/cancel" element={<PaymentCallbackPage />} />
        <Route path="/order/:orderId" element={<OrderStatusPage />} />
        
        {/* Legacy routes with just hotel slug */}
        <Route path="/:hotelSlug/welcome" element={<WelcomePage />} />
        <Route path="/:hotelSlug/housekeeping" element={<HousekeepingPage />} />
        <Route path="/:hotelSlug/maintenance" element={<MaintenancePage />} />
        <Route path="/:hotelSlug/laundry" element={<LaundryPage />} />
        <Route path="/:hotelSlug/guides" element={<GuidesPage />} />
        <Route path="/:hotelSlug/voice-call" element={<VoiceCallPage />} />
        <Route path="/:hotelSlug/folio" element={<FolioPage />} />
        <Route path="/:hotelSlug/payment/success" element={<PaymentCallbackPage />} />
        <Route path="/:hotelSlug/payment/error" element={<PaymentCallbackPage />} />
        <Route path="/:hotelSlug/payment/cancel" element={<PaymentCallbackPage />} />
        <Route path="/:hotelSlug/order/:orderId" element={<OrderStatusPage />} />
        
        <Route path="*" element={<Navigate to={`${basePath}/welcome`} replace />} />
      </Routes>
    </Layout>
    </>
  );
}

export default App;
